import { Link } from "react-router-dom";
import { TrendingUp, Search, Target, Mail, Share2, LineChart, Check, ArrowRight, PenTool } from "lucide-react";
import Navbar from "../../components/Navbar";
import PageMeta from "../../components/PageMeta";
import Footer from "../../components/Footer";
import FloatingFAQ from "../../components/FloatingFAQ";
import ConsultationForm from "../../components/ConsultationForm";

const features = [
  { icon: Search, title: "SEO Optimization", description: "Climb search rankings and drive organic traffic." },
  { icon: Target, title: "Paid Advertising", description: "Strategic PPC campaigns on Google, Meta, and LinkedIn." },
  { icon: PenTool, title: "Content Strategy", description: "Engaging content that establishes authority." },
  { icon: Mail, title: "Email Marketing", description: "Automated campaigns that nurture leads." },
  { icon: Share2, title: "Social Media", description: "Build your brand presence across platforms." },
  { icon: LineChart, title: "Analytics & Reporting", description: "Data-driven insights to optimize every dollar." }
];

const services = [
  { title: "SEO Services", description: "Comprehensive search engine optimization.", includes: ["Technical SEO Audit", "On-Page Optimization", "Link Building", "Local SEO", "Monthly Reporting"], price: "From $999/mo" },
  { title: "PPC Management", description: "Expertly managed paid advertising campaigns.", includes: ["Campaign Strategy", "Ad Copy & Creative", "Bid Management", "A/B Testing", "Conversion Tracking"], price: "From $1,499/mo" },
  { title: "Content Marketing", description: "Strategic content that attracts and converts.", includes: ["Content Strategy", "Blog Writing", "Infographics", "Video Scripts", "Distribution"], price: "From $1,999/mo" },
  { title: "Full-Service Marketing", description: "Complete digital marketing management.", includes: ["All Services Above", "Social Media Management", "Email Campaigns", "Marketing Automation", "Dedicated Strategist"], price: "From $3,999/mo" }
];

const results = [
  { metric: "300%", label: "Average Traffic Increase" },
  { metric: "150%", label: "Lead Generation Growth" },
  { metric: "85%", label: "Client Retention Rate" },
  { metric: "4.2x", label: "Average ROI" }
];

const marketingProcess = [
  { step: "01", title: "Audit", description: "We analyze your current marketing and competitors." },
  { step: "02", title: "Strategy", description: "Custom marketing plan aligned with your goals." },
  { step: "03", title: "Execute", description: "Our team implements campaigns across all channels." },
  { step: "04", title: "Optimize", description: "Continuous testing and refinement for results." }
];

export default function GrowthMarketing() {
  return (
    <div className="noise-bg">
      <PageMeta
        title="Growth Marketing, SEO & PPC | Averoan Technologies"
        description="Data-driven SEO, paid advertising, and content strategies that deliver real ROI."
      />
      <Navbar />

      <section className="pt-32 pb-16 relative" data-testid="marketing-hero">
        <div className="hero-glow absolute top-0 right-0 opacity-30" />
        <div className="container-main relative z-10">
          <div className="max-w-4xl">
            <span className="tag mb-6">Growth Marketing</span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6" style={{ fontFamily: '"Clash Display", sans-serif' }}>
              Marketing that<br /><span style={{ color: '#D4FF00' }}>drives growth</span>
            </h1>
            <p className="text-base md:text-lg max-w-xl mb-8" style={{ color: '#A1A1AA' }}>
              SEO, paid advertising, and content strategies that put your business in front of the right audience.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#get-started" className="btn-primary" data-testid="marketing-cta">Get a Free Audit <ArrowRight size={16} /></a>
              <Link to="/portfolio" className="btn-outline">See Results</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16" style={{ background: '#18181B' }}>
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {results.map((result) => (
              <div key={result.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold mb-2" style={{ fontFamily: '"Clash Display", sans-serif', color: '#D4FF00' }}>{result.metric}</div>
                <div className="text-sm" style={{ color: '#A1A1AA' }}>{result.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">Capabilities</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>Full-funnel marketing</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="card">
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(212, 255, 0, 0.1)', border: '1px solid rgba(212, 255, 0, 0.2)' }}>
                    <feature.icon size={22} style={{ color: '#D4FF00' }} />
                  </div>
                  <h3 className="text-lg font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{feature.title}</h3>
                  <p className="text-sm" style={{ color: '#A1A1AA' }}>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding" style={{ background: '#18181B' }}>
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">Packages</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>Marketing services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service) => (
              <div key={service.title} className="p-8 rounded-xl bg-[#0F0F11]" style={{ border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{service.title}</h3>
                  <span style={{ color: '#D4FF00', fontSize: '14px', fontWeight: '600' }}>{service.price}</span>
                </div>
                <p className="mb-6" style={{ color: '#A1A1AA' }}>{service.description}</p>
                <ul className="space-y-2">
                  {service.includes.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm" style={{ color: '#A1A1AA' }}>
                      <Check size={14} style={{ color: '#D4FF00' }} />{item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">Our Approach</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>How we deliver results</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {marketingProcess.map((item) => (
              <div key={item.step}>
                <div className="text-5xl font-bold mb-4" style={{ fontFamily: '"Clash Display", sans-serif', color: '#D4FF00' }}>{item.step}</div>
                <h3 className="text-xl font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{item.title}</h3>
                <p className="text-sm" style={{ color: '#A1A1AA' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      <section id="get-started" className="section-padding" style={{ background: '#18181B' }} data-testid="marketing-consultation">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="tag mb-4">Get Started</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>
              Ready to <span style={{ color: '#D4FF00' }}>grow?</span>
            </h2>
            <p className="text-base md:text-lg mt-4" style={{ color: '#A1A1AA' }}>
              Get a free marketing audit and book a consultation.
            </p>
          </div>
          <ConsultationForm preSelectedServiceId="marketing" />
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
}
